import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import EmailPreviewSection from "./EmailPreviewSection";

type ExpirationValue = "24h" | "3d" | "7d";
type DeliveryMethod = "copy" | "email";
type FlowType = "VIDEO" | "WIDGET";

interface RequestFeedbackDialogProps {
  open: boolean;
  onClose: () => void;
  isLoading: boolean;
  bugKey?: string;
  flowType: FlowType;
  setFlowType: (v: FlowType) => void;
  includeAudio: boolean;
  setIncludeAudio: (v: boolean) => void;
  expiration: ExpirationValue;
  setExpiration: (v: ExpirationValue) => void;
  messageToCustomer: string;
  setMessageToCustomer: (v: string) => void;
  deliveryMethod: DeliveryMethod;
  setDeliveryMethod: (v: DeliveryMethod) => void;
  customerEmail: string;
  setCustomerEmail: (v: string) => void;
  customerUrl: string;
  projectDomain?: string;
  onGenerate: () => void;
  onGenerateWidget: () => void;
}

const RequestFeedbackDialog = ({
  open,
  onClose,
  isLoading,
  bugKey,
  flowType,
  setFlowType,
  includeAudio,
  setIncludeAudio,
  expiration,
  setExpiration,
  messageToCustomer,
  setMessageToCustomer,
  deliveryMethod,
  setDeliveryMethod,
  customerEmail,
  setCustomerEmail,
  customerUrl,
  projectDomain,
  onGenerate,
  onGenerateWidget,
}: RequestFeedbackDialogProps) => {
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      slotProps={{
        paper: {
          sx: {
            borderRadius: "16px",
            backgroundColor: theme.palette.background.paper,
            overflow: "hidden",
            border: `1px solid ${theme.palette.divider}`,
          },
        },
      }}
    >
      {/* ════ Header ════ */}
      <Box
        sx={{
          px: 2.5,
          py: 1.8,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: isDark
                ? `${theme.palette.primary.main}20`
                : `${theme.palette.primary.main}12`,
            }}
          >
            <VideocamOutlinedIcon
              sx={{ fontSize: 20, color: "primary.main" }}
            />
          </Box>
          <Box>
            <Typography
              fontWeight={700}
              color="text.primary"
              fontSize="15px"
              sx={{ lineHeight: 1.3 }}
            >
              {t("Request Customer Feedback")}
            </Typography>
            <Typography color="text.secondary" fontSize="11.5px">
              {t("Generate a feedback link for ticket")}{" "}
              <Box
                component="span"
                sx={{
                  fontWeight: 700,
                  color: "primary.main",
                  fontFamily: "monospace",
                  fontSize: "11px",
                }}
              >
                {bugKey}
              </Box>
            </Typography>
          </Box>
        </Stack>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            color: "text.disabled",
            width: 28,
            height: 28,
            "&:hover": { color: "text.secondary" },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 2, pb: 2, px: 2.5 }}>
        {/* ════ Loading State ════ */}
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 5,
              px: 2,
            }}
          >
            <Box sx={{ position: "relative", width: 72, height: 72, mb: 2.5 }}>
              <CircularProgress
                size={72}
                thickness={2.5}
                sx={{ color: theme.palette.primary.main, position: "absolute" }}
              />
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: isDark
                      ? `${theme.palette.primary.main}18`
                      : `${theme.palette.primary.main}10`,
                  }}
                >
                  {flowType === "WIDGET" ? (
                    <WidgetsOutlinedIcon
                      sx={{ fontSize: 24, color: "primary.main" }}
                    />
                  ) : (
                    <VideocamOutlinedIcon
                      sx={{ fontSize: 24, color: "primary.main" }}
                    />
                  )}
                </Box>
              </Box>
            </Box>

            <Typography fontWeight={700} fontSize="15px" sx={{ mb: 0.5 }}>
              {t("Generating your link...")}
            </Typography>
            <Typography
              color="text.secondary"
              fontSize="12px"
              sx={{
                mb: 3,
                textAlign: "center",
                maxWidth: 320,
                lineHeight: 1.6,
              }}
            >
              {t(
                "Setting up a secure feedback session for your customer. This usually takes a few seconds.",
              )}
            </Typography>

            <Stack spacing={1} sx={{ width: "100%", maxWidth: 340 }}>
              {[
                {
                  icon: <LockOutlinedIcon sx={{ fontSize: 16 }} />,
                  text: t("Creating secure token"),
                  color: "#7c3aed",
                },
                {
                  icon: <CloudUploadOutlinedIcon sx={{ fontSize: 16 }} />,
                  text: t("Preparing upload storage"),
                  color: "#059669",
                },
                {
                  icon:
                    deliveryMethod === "email" ? (
                      <EmailOutlinedIcon sx={{ fontSize: 16 }} />
                    ) : (
                      <ContentCopyIcon sx={{ fontSize: 16 }} />
                    ),
                  text:
                    deliveryMethod === "email"
                      ? t("Sending email to customer")
                      : t("Copying link to clipboard"),
                  color: "#3b82f6",
                },
              ].map((step, i) => (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.2,
                    px: 1.5,
                    py: 1,
                    borderRadius: "8px",
                    border: `1px solid ${theme.palette.divider}`,
                    bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#fafafa",
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "7px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: isDark ? `${step.color}18` : `${step.color}12`,
                      color: step.color,
                      flexShrink: 0,
                    }}
                  >
                    {step.icon}
                  </Box>
                  <Typography
                    color="text.secondary"
                    fontSize="12px"
                    sx={{ flex: 1 }}
                  >
                    {step.text}
                  </Typography>
                  <CircularProgress
                    size={14}
                    thickness={4}
                    sx={{ color: step.color, flexShrink: 0 }}
                  />
                </Box>
              ))}
            </Stack>
          </Box>
        ) : (
          /* ════ Configuration Form ════ */
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* ── Info Banner ── */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                borderRadius: "8px",
                px: 1.5,
                py: 1,
                bgcolor: isDark ? "rgba(99,102,241,0.06)" : "#eef2ff",
                border: `1px solid ${isDark ? "rgba(99,102,241,0.12)" : "#e0e7ff"}`,
              }}
            >
              <InfoOutlinedIcon
                sx={{ color: "#6366f1", fontSize: 15, mt: 0.15, flexShrink: 0 }}
              />
              <Typography
                color="text.secondary"
                fontSize="11.5px"
                sx={{ lineHeight: 1.55 }}
              >
                {flowType === "VIDEO"
                  ? t(
                      "Your customer records or screenshots the issue and it auto-attaches here.",
                    )
                  : t(
                      "Your customer visits their site with the widget enabled and submits feedback.",
                    )}
              </Typography>
            </Box>

            {/* ── Section 1: Feedback Type ── */}
            <FormSection
              icon={<VideocamOutlinedIcon sx={{ fontSize: 14 }} />}
              title={t("Feedback Type")}
              isDark={isDark}
            >
              <Stack direction="row" spacing={1.2}>
                <SelectionCard
                  selected={flowType === "VIDEO"}
                  onClick={() => setFlowType("VIDEO")}
                  icon={<VideocamOutlinedIcon sx={{ fontSize: 17 }} />}
                  title={t("Recording Link")}
                  description={t(
                    "Customer records their screen directly in the browser",
                  )}
                  isDark={isDark}
                  primaryColor={theme.palette.primary.main}
                />
                <SelectionCard
                  selected={flowType === "WIDGET"}
                  onClick={() => setFlowType("WIDGET")}
                  icon={<WidgetsOutlinedIcon sx={{ fontSize: 17 }} />}
                  title={t("Advanced Recording")}
                  description={t(
                    "Embeds a feedback widget on the customer's site",
                  )}
                  isDark={isDark}
                  primaryColor={theme.palette.primary.main}
                />
              </Stack>

              {/* VIDEO-only: Include Audio */}
              {flowType === "VIDEO" && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 1.2,
                    py: 0.6,
                    mt: 1.2,
                    borderRadius: "7px",
                    border: `1px solid ${theme.palette.divider}`,
                    bgcolor: isDark ? "rgba(255,255,255,0.015)" : "#fcfcfd",
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.8}>
                    <Box
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: "6px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: isDark
                          ? `${theme.palette.primary.main}18`
                          : `${theme.palette.primary.main}12`,
                        color: "primary.main",
                        flexShrink: 0,
                      }}
                    >
                      <MicNoneOutlinedIcon sx={{ fontSize: 14 }} />
                    </Box>
                    <Box>
                      <Typography
                        fontWeight={600}
                        fontSize="11.5px"
                        lineHeight={1.3}
                      >
                        {t("Include Audio")}
                      </Typography>
                      <Typography
                        color="text.disabled"
                        fontSize="9.5px"
                        lineHeight={1.3}
                      >
                        {t("Allow customer to record microphone audio")}
                      </Typography>
                    </Box>
                  </Stack>
                  <Switch
                    size="small"
                    checked={includeAudio}
                    onChange={(e) => setIncludeAudio(e.target.checked)}
                  />
                </Box>
              )}

              {/* WIDGET-only: Customer Website URL */}
              {flowType === "WIDGET" && (
                <TextField
                  fullWidth
                  size="small"
                  label={t("Customer Website URL")}
                  placeholder="https://example.com/page"
                  value={projectDomain}
                  sx={{
                    mt: 1.2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "7px",
                      fontSize: "12px",
                    },
                    "& .MuiInputLabel-root": { fontSize: "11.5px" },
                  }}
                  disabled
                />
              )}
            </FormSection>

            {/* ── Section 2: Configuration ── */}
            <FormSection
              icon={<TuneOutlinedIcon sx={{ fontSize: 14 }} />}
              title={t("Configuration")}
              isDark={isDark}
            >
              {/* Expiration */}
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  fontSize="10px"
                  fontWeight={600}
                  color="text.disabled"
                  textTransform="uppercase"
                  letterSpacing={0.6}
                  sx={{ mb: 0.6 }}
                >
                  {t("Link Expires In")}
                </Typography>
                <Stack direction="row" spacing={0.6}>
                  {(["24h", "3d", "7d"] as ExpirationValue[]).map((val) => {
                    const label =
                      val === "24h"
                        ? t("24 hours")
                        : val === "3d"
                          ? t("3 days")
                          : t("7 days");
                    const isSelected = expiration === val;
                    return (
                      <Chip
                        key={val}
                        icon={
                          isSelected ? (
                            <AccessTimeIcon sx={{ fontSize: 13 }} />
                          ) : undefined
                        }
                        label={label}
                        size="small"
                        onClick={() => setExpiration(val)}
                        clickable
                        sx={{
                          fontSize: "10.5px",
                          fontWeight: isSelected ? 700 : 500,
                          height: 26,
                          borderRadius: "6px",
                          bgcolor: isSelected
                            ? isDark
                              ? `${theme.palette.primary.main}20`
                              : `${theme.palette.primary.main}12`
                            : "transparent",
                          color: isSelected
                            ? theme.palette.primary.main
                            : "text.secondary",
                          border: `1px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
                          "& .MuiChip-icon": {
                            color: theme.palette.primary.main,
                            ml: 0.3,
                          },
                          "&:hover": {
                            bgcolor: isSelected
                              ? isDark
                                ? `${theme.palette.primary.main}25`
                                : `${theme.palette.primary.main}18`
                              : isDark
                                ? "rgba(255,255,255,0.04)"
                                : "#f5f5f5",
                          },
                        }}
                      />
                    );
                  })}
                </Stack>
              </Box>

              {/* Message to Customer */}
              <Box>
                <Typography
                  fontSize="10px"
                  fontWeight={600}
                  color="text.disabled"
                  textTransform="uppercase"
                  letterSpacing={0.6}
                  sx={{
                    mb: 0.6,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.4,
                  }}
                >
                  <ChatOutlinedIcon sx={{ fontSize: 12 }} />
                  {t("Message to Customer")}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  maxRows={4}
                  placeholder={t(
                    "Describe what you need the customer to show or do...",
                  )}
                  value={messageToCustomer}
                  onChange={(e) => setMessageToCustomer(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "7px",
                      fontSize: "12px",
                      lineHeight: 1.6,
                    },
                  }}
                />
              </Box>
            </FormSection>

            {/* ── Section 3: Delivery ── */}
            <FormSection
              icon={<SendOutlinedIcon sx={{ fontSize: 14 }} />}
              title={t("Delivery Method")}
              isDark={isDark}
            >
              <Stack direction="row" spacing={1}>
                <DeliveryCard
                  selected={deliveryMethod === "copy"}
                  onClick={() => setDeliveryMethod("copy")}
                  icon={<ContentCopyIcon />}
                  title={t("Copy Link")}
                  description={t("Copy to clipboard")}
                  isDark={isDark}
                  primaryColor={theme.palette.primary.main}
                />
                <DeliveryCard
                  selected={deliveryMethod === "email"}
                  onClick={() => setDeliveryMethod("email")}
                  icon={<EmailOutlinedIcon />}
                  title={t("Send via Email")}
                  description={t("Send directly to customer")}
                  isDark={isDark}
                  primaryColor={theme.palette.primary.main}
                />
              </Stack>

              {deliveryMethod === "email" && (
                <TextField
                  fullWidth
                  size="small"
                  type="email"
                  label={t("Customer Email")}
                  placeholder="customer@example.com"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  required
                  sx={{
                    mt: 1.2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "7px",
                      fontSize: "12px",
                    },
                    "& .MuiInputLabel-root": { fontSize: "11.5px" },
                  }}
                />
              )}
            </FormSection>

            {deliveryMethod === "email" && (
              <EmailPreviewSection
                flowType={flowType}
                messageToCustomer={messageToCustomer}
              />
            )}
          </Box>
        )}
      </DialogContent>

      {/* ════ Footer ════ */}
      {!isLoading && (
        <>
          <Divider />
          <Box
            sx={{
              px: 2.5,
              py: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              bgcolor: isDark ? "rgba(255,255,255,0.015)" : "#fafafa",
            }}
          >
            <Button
              onClick={onClose}
              size="small"
              sx={{
                textTransform: "none",
                fontSize: "12px",
                fontWeight: 600,
                color: "text.secondary",
                borderRadius: "7px",
                px: 2,
                "&:hover": {
                  bgcolor: isDark ? "rgba(255,255,255,0.04)" : "#f0f0f0",
                },
              }}
            >
              {t("Cancel")}
            </Button>
            <LoadingButton
              variant="contained"
              size="small"
              disableElevation
              startIcon={
                deliveryMethod === "copy" ? (
                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                ) : (
                  <SendOutlinedIcon sx={{ fontSize: 14 }} />
                )
              }
              onClick={flowType === "WIDGET" ? onGenerateWidget : onGenerate}
              disabled={
                (deliveryMethod === "email" && !customerEmail.trim()) ||
                (flowType === "WIDGET" && !customerUrl.trim())
              }
              sx={{
                textTransform: "none",
                fontSize: "12px",
                fontWeight: 700,
                borderRadius: "8px",
                px: 2.5,
                py: 0.7,
                boxShadow: "none",
              }}
            >
              {deliveryMethod === "copy"
                ? t("Generate & Copy")
                : t("Generate & Send")}
            </LoadingButton>
          </Box>
        </>
      )}
    </Dialog>
  );
};

/* ─── Form Section (grouped panel) ──────────────────────────────────────────── */

function FormSection({
  icon,
  title,
  isDark,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  isDark: boolean;
  children: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        borderRadius: "10px",
        border: (theme) => `1px solid ${theme.palette.divider}`,
        overflow: "hidden",
      }}
    >
      {/* Section header bar */}
      <Box
        sx={{
          px: 1.5,
          py: 0.7,
          display: "flex",
          alignItems: "center",
          gap: 0.6,
          bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#f8f8fa",
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ color: "text.disabled", display: "flex" }}>{icon}</Box>
        <Typography
          fontSize="10.5px"
          fontWeight={700}
          letterSpacing={0.5}
          textTransform="uppercase"
          color="text.secondary"
        >
          {title}
        </Typography>
      </Box>
      {/* Section content */}
      <Box sx={{ p: 1.5 }}>{children}</Box>
    </Box>
  );
}

/* ─── Selection Card (Feedback Type) ────────────────────────────────────────── */

function SelectionCard({
  selected,
  onClick,
  icon,
  title,
  description,
  isDark,
  primaryColor,
}: {
  selected: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  description: string;
  isDark: boolean;
  primaryColor: string;
}) {
  return (
    <Box
      onClick={onClick}
      sx={{
        flex: 1,
        cursor: "pointer",
        borderRadius: "8px",
        p: 1.2,
        border: `1.5px solid ${selected ? primaryColor : isDark ? "rgba(255,255,255,0.08)" : "#e5e7eb"}`,
        bgcolor: selected
          ? isDark
            ? `${primaryColor}0C`
            : `${primaryColor}06`
          : "transparent",
        transition: "all 0.15s ease",
        "&:hover": {
          borderColor: selected
            ? primaryColor
            : isDark
              ? "rgba(255,255,255,0.15)"
              : "#d1d5db",
          bgcolor: selected
            ? isDark
              ? `${primaryColor}0C`
              : `${primaryColor}06`
            : isDark
              ? "rgba(255,255,255,0.02)"
              : "#fafafa",
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.7} sx={{ mb: 0.3 }}>
        {selected ? (
          <CheckCircleOutlineIcon sx={{ fontSize: 15, color: primaryColor }} />
        ) : (
          <RadioButtonUncheckedIcon
            sx={{ fontSize: 15, color: "text.disabled" }}
          />
        )}
        <Box
          sx={{
            color: selected ? primaryColor : "text.secondary",
            display: "flex",
          }}
        >
          {icon}
        </Box>
        <Typography
          fontSize="11.5px"
          fontWeight={selected ? 700 : 600}
          color={selected ? primaryColor : "text.primary"}
        >
          {title}
        </Typography>
      </Stack>
      <Typography
        color="text.disabled"
        fontSize="9.5px"
        sx={{ display: "block", pl: 3, lineHeight: 1.45 }}
      >
        {description}
      </Typography>
    </Box>
  );
}

/* ─── Delivery Card ─────────────────────────────────────────────────────────── */

function DeliveryCard({
  selected,
  onClick,
  icon,
  title,
  description,
  isDark,
  primaryColor,
}: {
  selected: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  description: string;
  isDark: boolean;
  primaryColor: string;
}) {
  return (
    <Box
      onClick={onClick}
      sx={{
        flex: 1,
        cursor: "pointer",
        borderRadius: "7px",
        px: 1.2,
        py: 0.7,
        display: "flex",
        alignItems: "center",
        gap: 0.8,
        border: `1.5px solid ${selected ? primaryColor : isDark ? "rgba(255,255,255,0.08)" : "#e5e7eb"}`,
        bgcolor: selected
          ? isDark
            ? `${primaryColor}0C`
            : `${primaryColor}06`
          : "transparent",
        transition: "all 0.15s ease",
        "&:hover": {
          borderColor: selected
            ? primaryColor
            : isDark
              ? "rgba(255,255,255,0.15)"
              : "#d1d5db",
          bgcolor: selected
            ? isDark
              ? `${primaryColor}0C`
              : `${primaryColor}06`
            : isDark
              ? "rgba(255,255,255,0.02)"
              : "#fafafa",
        },
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: "7px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          bgcolor: selected
            ? isDark
              ? `${primaryColor}18`
              : `${primaryColor}10`
            : isDark
              ? "rgba(255,255,255,0.05)"
              : "#f3f4f6",
          color: selected ? primaryColor : "text.secondary",
          "& svg": { fontSize: 15 },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          fontSize="11px"
          fontWeight={selected ? 700 : 600}
          color={selected ? primaryColor : "text.primary"}
          lineHeight={1.3}
        >
          {title}
        </Typography>
        <Typography color="text.disabled" fontSize="9.5px" lineHeight={1.3}>
          {description}
        </Typography>
      </Box>
    </Box>
  );
}

export default RequestFeedbackDialog;
